import { supabase } from "@/integrations/supabase/client";

/**
 * Rastreamento de origem (externa) e jornada (interna) das visitantes do site.
 * - Origem externa: UTMs, click ids (fbclid/ttclid/gclid) e referrer, guardados
 *   como "first touch" (primeiro contato) e "last touch" (último contato).
 * - Jornada interna: eventos de navegação (page_view, product_view, add_to_cart,
 *   checkout_start, purchase) gravados na tabela site_events.
 */

const VISITOR_KEY = "miahma-visitor-id";
const FIRST_TOUCH_KEY = "miahma-attr-first";
const LAST_TOUCH_KEY = "miahma-attr-last";
const SESSION_KEY = "miahma-session-id";
const ATTR_TTL_DAYS = 30;

export interface Touch {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  click_id?: string | null;
  referrer?: string | null;
  channel: string;
  landing_page?: string | null;
  at: string;
}

export interface Attribution {
  first?: Touch;
  last?: Touch;
  visitor_id?: string;
}

const uid = () =>
  (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const safeGet = (storage: Storage, key: string) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};
const safeSet = (storage: Storage, key: string, value: string) => {
  try {
    storage.setItem(key, value);
  } catch {
    /* ignore */
  }
};

export const getVisitorId = (): string => {
  if (typeof window === "undefined") return "ssr";
  let id = safeGet(localStorage, VISITOR_KEY);
  if (!id) {
    id = uid();
    safeSet(localStorage, VISITOR_KEY, id);
  }
  return id;
};

export const getSessionId = (): string => {
  if (typeof window === "undefined") return "ssr";
  let id = safeGet(sessionStorage, SESSION_KEY);
  if (!id) {
    id = uid();
    safeSet(sessionStorage, SESSION_KEY, id);
  }
  return id;
};

/** Classifica a origem em um canal legível (instagram, meta_ads, google, etc.). */
export const classifyChannel = (t: Partial<Touch>): string => {
  const src = (t.utm_source || "").toLowerCase();
  const medium = (t.utm_medium || "").toLowerCase();
  const ref = (t.referrer || "").toLowerCase();
  const paid = /cpc|ppc|paid|ads|anuncio|paid_social/.test(medium);

  if (src.includes("instagram") || ref.includes("instagram")) return paid ? "meta_ads" : "instagram";
  if (src.includes("facebook") || ref.includes("facebook") || ref.includes("fb.")) return paid ? "meta_ads" : "facebook";
  if (src.includes("tiktok") || ref.includes("tiktok")) return paid ? "tiktok_ads" : "tiktok";
  if (src.includes("google") || ref.includes("google")) return paid ? "google_ads" : "google";
  if (src.includes("whatsapp") || ref.includes("whatsapp") || ref.includes("wa.me")) return "whatsapp";
  if (src.includes("email") || medium.includes("email")) return "email";
  if (src.includes("sms")) return "sms";
  if (src) return src;
  if (ref) {
    try {
      const host = new URL(t.referrer as string).hostname.replace(/^www\./, "");
      if (host && !host.includes("miahma")) return host;
    } catch {
      /* ignore */
    }
    return "referral";
  }
  return "direto";
};

/** Próprio site (www/apex/subdomínios) e retorno do pagamento/3DS não são origem nova — não sobrescrevem o último contato. */
export const isIgnoredReferrerHost = (refHost: string, currentHost: string): boolean => {
  const h = refHost.toLowerCase();
  const strip = (x: string) => x.toLowerCase().replace(/^www\./, "");
  if (strip(h) === strip(currentHost)) return true;
  return /(^|\.)miahma\.com\.br$/.test(h) || /(^|\.)appmax\.com\.br$/.test(h) || /(^|\.)(cardinalcommerce|3dsecure|securecode)\./.test(h);
};

const parseTouch = (): Touch | null => {
  const params = new URLSearchParams(window.location.search);
  const get = (k: string) => params.get(k) || null;
  const clickId =
    get("fbclid") || get("ttclid") || get("gclid") || get("gbraid") || get("wbraid");
  const rawRef = document.referrer || null;
  const isInternalRef = (() => {
    if (!rawRef) return false;
    try {
      return isIgnoredReferrerHost(new URL(rawRef).hostname, window.location.hostname);
    } catch {
      return false;
    }
  })();
  const referrer = isInternalRef ? null : rawRef;

  const hasUtm = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].some((k) =>
    params.get(k),
  );
  if (!hasUtm && !clickId && !referrer) return null;

  const base: Partial<Touch> = {
    utm_source: get("utm_source"),
    utm_medium: get("utm_medium"),
    utm_campaign: get("utm_campaign"),
    utm_content: get("utm_content"),
    utm_term: get("utm_term"),
    click_id: clickId,
    referrer,
  };
  return {
    ...base,
    channel: classifyChannel(base),
    landing_page: window.location.pathname,
    at: new Date().toISOString(),
  } as Touch;
};

const readStored = (key: string): Touch | null => {
  const raw = safeGet(localStorage, key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Touch;
    const age = Date.now() - new Date(parsed.at).getTime();
    if (age > ATTR_TTL_DAYS * 86400000) return null;
    return parsed;
  } catch {
    return null;
  }
};

/** Deve ser chamado uma vez no carregamento do app. */
export const captureAttribution = () => {
  if (typeof window === "undefined") return;
  getVisitorId();
  getSessionId();
  const touch = parseTouch();
  if (!touch) return;
  safeSet(localStorage, LAST_TOUCH_KEY, JSON.stringify(touch));
  if (!readStored(FIRST_TOUCH_KEY)) {
    safeSet(localStorage, FIRST_TOUCH_KEY, JSON.stringify(touch));
  }
};

export const getAttribution = (): Attribution => {
  if (typeof window === "undefined") return {};
  const first = readStored(FIRST_TOUCH_KEY);
  const last = readStored(LAST_TOUCH_KEY);
  const fallback: Touch = {
    channel: "direto",
    referrer: null,
    landing_page: window.location.pathname,
    at: new Date().toISOString(),
  };
  return {
    visitor_id: getVisitorId(),
    first: first || last || fallback,
    last: last || first || fallback,
  };
};

const deviceType = () => {
  if (typeof window === "undefined") return null;
  return window.innerWidth < 768 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop";
};

export type SiteEventType =
  | "page_view"
  | "product_view"
  | "product_color_select"
  | "product_size_select"
  | "add_to_cart"
  | "checkout_start"
  | "checkout_customer_filled"
  | "checkout_cep_filled"
  | "shipping_quote"
  | "checkout_shipping_selected"
  | "checkout_payment_selected"
  | "payment_attempt"
  | "payment_result"
  | "pix_generated"
  | "order_created"
  | "postsale_account_activated"
  | "postsale_password_created"
  | "postsale_birthday_filled"
  | "postsale_profile_completed"
  | "postsale_review_submitted"
  | "postsale_cashback_used";

export interface TrackPayload {
  path?: string | null;
  sourcePage?: string | null;
  productId?: string | null;
  colorVariantId?: string | null;
  productName?: string | null;
  quantity?: number | null;
  value?: number | null;
  orderId?: string | null;
  /** Mesma ação = mesmo id (ex.: `pix:<pedido>`); o servidor ignora repetidos. */
  eventId?: string;
  metadata?: Record<string, unknown>;
}

const isUuid = (v?: string | null) =>
  !!v && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);

/** Só chaves conhecidas e valores simples; nada com cara de e-mail, telefone, CPF ou CEP. */
const META_KEYS = new Set([
  "size", "color", "method", "outcome", "service", "services", "min_value", "min_days", "buy_now",
  "source", "list", "position", "step", "attempt", "status", "media", "rating", "has_media", "layout", "value_shipping",
]);
export const sanitizeMetadata = (meta: Record<string, unknown> = {}): Record<string, string | number | boolean> => {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(meta)) {
    if (!META_KEYS.has(k)) continue;
    if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
    else if (typeof v === "boolean") out[k] = v;
    else if (typeof v === "string" && v.length <= 60 && !/@/.test(v) && !/\d{7,}/.test(v) && !/\d{3}[.\- ]\d{3}/.test(v)) out[k] = v;
  }
  return out;
};

/** Origem do referrer só com o domínio (a URL completa pode carregar dados pessoais). */
const refOrigin = (ref?: string | null) => {
  if (!ref) return null;
  try { return new URL(ref).origin; } catch { return null; }
};

const CONSENT_KEY = "miahma_lgpd_consent";
/** Respeita recusa registrada no consentimento existente (hoje o aviso só registra aceite). */
const BOT_UA = /bot|crawl|spider|slurp|headless|lighthouse|pagespeed|prerender|preview|facebookexternalhit|whatsapp|bingpreview|google-inspectiontool|chrome-lighthouse/i;
const PREVIEW_HOST = /(^|\.)id-preview--|lovableproject\.com$|^preview--/i;
/** Robôs, navegador automatizado e ambientes de prévia/painel nunca entram nas métricas do site. */
export const isExcludedTraffic = (nav: { userAgent?: string; webdriver?: boolean } = typeof navigator !== "undefined" ? navigator : {}, host = typeof location !== "undefined" ? location.hostname : ""): boolean =>
  !!nav.webdriver || BOT_UA.test(nav.userAgent || "") || PREVIEW_HOST.test(host) || /^painel\./i.test(host);

export const trackingAllowed = (): boolean => {
  if (isExcludedTraffic()) return false;
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return true;
    const c = JSON.parse(raw) as { accepted?: boolean; analytics?: boolean };
    return c.accepted !== false && c.analytics !== false;
  } catch {
    return true;
  }
};

type QueuedEvent = Record<string, unknown>;
let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
const sentIds = new Set<string>();

const RPC_URL = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/site_track_events`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

/** Envia a fila em lotes de até 25. Nunca lança erro. */
export const flushEvents = (useBeacon = false) => {
  if (timer) { clearTimeout(timer); timer = null; }
  while (queue.length) {
    const batch = queue.splice(0, 25);
    try {
      if (useBeacon && typeof fetch === "function") {
        void fetch(RPC_URL, {
          method: "POST",
          keepalive: true,
          headers: { "Content-Type": "application/json", apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
          body: JSON.stringify({ p_events: batch }),
        }).catch(() => undefined);
      } else {
        void Promise.resolve(supabase.rpc("site_track_events" as never, { p_events: batch } as never))
          .then((r: { error?: { message?: string } | null } | undefined) => { if (r?.error) console.debug("[tracking] falhou", r.error.message); })
          .catch(() => undefined);
      }
    } catch { /* ignore */ }
  }
};

let listenersBound = false;
const bindListeners = () => {
  if (listenersBound || typeof window === "undefined") return;
  listenersBound = true;
  const onHide = () => { if (queue.length) flushEvents(true); };
  window.addEventListener("pagehide", onHide);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") onHide(); });
};

/** Grava um evento de jornada (fila + lote). Nunca lança erro nem bloqueia a UI. */
export const trackEvent = (eventType: SiteEventType, payload: TrackPayload = {}) => {
  if (typeof window === "undefined") return;
  try {
    if (!trackingAllowed()) return;
    const eventId = payload.eventId && /^[A-Za-z0-9:_-]{8,120}$/.test(payload.eventId) ? payload.eventId : `e:${uid()}`;
    if (sentIds.has(eventId)) return;
    sentIds.add(eventId);
    const last = getAttribution().last;
    queue.push({
      event_id: eventId,
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      event_type: eventType,
      path: (payload.path ?? window.location.pathname).split(/[?#]/)[0],
      source_page: payload.sourcePage ?? null,
      product_id: isUuid(payload.productId) ? payload.productId : null,
      color_variant_id: isUuid(payload.colorVariantId) ? payload.colorVariantId : null,
      product_name: payload.productName ?? null,
      quantity: payload.quantity ?? null,
      value: typeof payload.value === "number" && Number.isFinite(payload.value) ? Math.round(payload.value * 100) / 100 : null,
      order_id: isUuid(payload.orderId) ? payload.orderId : null,
      referrer: refOrigin(last?.referrer),
      utm_source: last?.utm_source ?? null,
      utm_medium: last?.utm_medium ?? null,
      utm_campaign: last?.utm_campaign ?? null,
      utm_content: last?.utm_content ?? null,
      utm_term: last?.utm_term ?? null,
      click_id: last?.click_id ?? null,
      channel: last?.channel ?? "direto",
      device: deviceType(),
      metadata: sanitizeMetadata(payload.metadata),
    });
    bindListeners();
    if (queue.length >= 10) flushEvents();
    else if (!timer) timer = setTimeout(() => flushEvents(), 2000);
  } catch {
    /* tracking nunca quebra a experiência */
  }
};

/** Só para testes. */
export const __resetTrackingForTests = () => { queue = []; sentIds.clear(); if (timer) clearTimeout(timer); timer = null; };
export const __peekQueueForTests = () => queue.slice();
